import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /** @type {AbortController | undefined} */
  #controller;

  #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  #hasFocus = false;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('mouseenter', this.suspend, { signal });
    this.addEventListener('mouseleave', this.resume, { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    this.#reducedMotion.addEventListener('change', this.#handleMotionChange, { signal });
    this.current = this.#current;
    this.resume();
  }

  updatedCallback() {
    super.updatedCallback();
    this.current = this.#current;
    this.suspend();
    this.resume();
  }

  disconnectedCallback() {
    this.suspend();
    this.#controller?.abort();
    this.#hasFocus = false;
    super.disconnectedCallback();
  }

  /** @param {Event} [event] - User activation; automatic advancement has no event. */
  next(event) {
    if (event instanceof Event) this.pause();
    this.current += 1;
    this.resume();
  }

  /** @param {Event} [event] - User activation. */
  previous(event) {
    if (event instanceof Event) this.pause();
    this.current -= 1;
    this.resume();
  }

  /**
   * Starts automatic slide playback.
   * @param {number | Event} [interval] - Delay in milliseconds, or a routed button event.
   */
  play(interval = this.autoplayInterval) {
    this.paused = false;
    this.suspend();
    const delay = typeof interval === 'number' ? interval : this.autoplayInterval;
    if (
      !this.isConnected || !delay || delay <= 0 || !this.autoplay || this.#reducedMotion.matches ||
      this.#hasFocus || this.matches(':hover') || document.hidden || (this.refs.slides?.length ?? 0) < 2
    ) return;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || this.#hasFocus || document.hidden) return;

      this.next();
    }, delay);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
    this.setAttribute('aria-live', paused ? 'polite' : 'off');
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.play();
  }

  get autoplay() {
    return this.hasAttribute('autoplay') && Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.refs.slides?.[this.#current]?.dataset.announcementInterval ?? this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value) || value <= 0) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    if (slides.length === 0) return;
    const relativeIndex = ((current % slides.length) + slides.length) % slides.length;
    this.#current = relativeIndex;

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
      slide.inert = index !== relativeIndex;
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #handleFocusIn = () => {
    this.#hasFocus = true;
    this.suspend();
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#hasFocus = false;
    this.resume();
  };

  #handleMotionChange = () => {
    this.suspend();
    this.resume();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
